import React from 'react'
import { useRouteMatch, useHistory } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { DateTime } from 'luxon'
import Container from '@material-ui/core/Container'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemAvatar from '@material-ui/core/ListItemAvatar'
import PersonIcon from '@material-ui/icons/Person'
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined'
import AccessTimeOutlinedIcon from '@material-ui/icons/AccessTimeOutlined'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'

import { PageHeader } from 'components/page-header'
import RouterLinkButton from 'components/router-link-button'
import Error from 'components/error'
import { FormattedOneLineAddress } from 'components/util/formatted_address'
import ContentSection from 'components/content-section'
import DeleteFacilityDialog from './delete-facility-dialog'
import { flags, useFeatureFlags } from 'lib/feature-flags'

const useStyles = makeStyles(theme => ({
  successCheck: {
    color: theme.palette.success.main,
  },
}))

const AvailabilityList = ({ availability }) => {
  const styles = useStyles()
  const daysOfWeek = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
  ]
  const aggregateByDay = timeSlots => {
    const initialValue = daysOfWeek.reduce((acc, d) => {
      acc[d] = []
      return acc
    }, {})
    return (timeSlots || []).reduce((acc, ts) => {
      acc[ts.day].push(ts)
      return acc
    }, initialValue)
  }

  const availabilityByDay = aggregateByDay(availability.timeSlots)

  const renderTimeslots = timeSlots => {
    if (!isEmpty(timeSlots)) {
      const slots = timeSlots.map(ts => {
        const startTime = DateTime.fromISO(ts.startTime).toFormat('h:mma')
        const endTime = DateTime.fromISO(ts.endTime).toFormat('h:mma')
        const formattedTimeSlot = `${startTime} - ${endTime}`
        return formattedTimeSlot
      })

      return slots.join(', ')
    } else {
      return 'Not Available'
    }
  }

  return (
    <>
      {daysOfWeek.map(day => {
        const timeSlots = availabilityByDay[day]
        return (
          <ListItem key={day}>
            <ListItemAvatar>
              {isEmpty(timeSlots) ? (
                <RemoveCircleIcon />
              ) : (
                <CheckCircleIcon className={styles.successCheck} />
              )}
            </ListItemAvatar>

            <ListItemText
              primary={day.toLowerCase()}
              secondary={renderTimeslots(timeSlots)}
              style={{ textTransform: 'capitalize' }}
            />
          </ListItem>
        )
      })}
    </>
  )
}

const Facility = ({ facility }) => {
  const { path } = useRouteMatch()
  const history = useHistory()
  const theme = useTheme()
  const [isCoachOnboardingEnabled] = useFeatureFlags([
    flags.FLAG_COACH_ONBOARDING,
  ])
  const {
    id,
    name,
    address1,
    address2,
    city,
    state,
    zip,
    timezone,
    url,
    externalFacilityId,
    availabilities,
    isPrivate,
  } = facility
  const facilityPath = path
    .split('/')
    .slice(0, isCoachOnboardingEnabled ? 4 : 3)
    .join('/')

  const crumbs = [
    {
      label: 'Home',
      to: path
        .split('/')
        .slice(0, 2)
        .join('/'),
    },
    {
      label: 'Facilities',
      to: facilityPath,
    },
  ]
  if (isCoachOnboardingEnabled) {
    crumbs.splice(1, 0, {
      label: 'Settings',
      to: path
        .split('/')
        .slice(0, 3)
        .join('/'),
    })
  }

  const hasAvailabilities = !isEmpty(availabilities)

  return (
    <>
      <PageHeader
        title={name}
        crumbs={crumbs}
        actions={
          <>
            {!externalFacilityId && (
              <RouterLinkButton
                variant="contained"
                color="primary"
                to={{
                  pathname: `${facilityPath}/${id}/edit`,
                }}
              >
                Edit
              </RouterLinkButton>
            )}
            <DeleteFacilityDialog
              facilityId={id}
              onSuccess={() => history.push('/pga-coach/facilities')}
              trigger={
                <Button
                  variant="outlined"
                  style={{
                    color: theme.palette.error.main,
                    borderColor: theme.palette.error.main,
                  }}
                >
                  Delete
                </Button>
              }
            />
          </>
        }
        fixedMobileActions
      />
      <Container>
        <ContentSection title="Facility Details">
          <ListItem>
            <ListItemAvatar>
              <PersonIcon />
            </ListItemAvatar>
            <ListItemText
              primary="Coach Profile"
              secondary={hasAvailabilities ? 'Active' : 'Inactive'}
            />
          </ListItem>

          <ListItem>
            <ListItemAvatar>
              <RoomOutlinedIcon />
            </ListItemAvatar>
            <ListItemText
              primary="Address"
              secondary={
                address1 ? (
                  <FormattedOneLineAddress
                    address1={address1}
                    address2={address2}
                    city={city}
                    state={state}
                    zip={zip}
                  />
                ) : (
                  url
                )
              }
            />
          </ListItem>

          <ListItem>
            <ListItemAvatar>
              <AccessTimeOutlinedIcon />
            </ListItemAvatar>
            <ListItemText primary="Time Zone" secondary={timezone} />
          </ListItem>

          {isPrivate && <ListItem>This is a private facility.</ListItem>}
        </ContentSection>
        <ContentSection title="Coaching Availability">
          {hasAvailabilities ? (
            <AvailabilityList availability={availabilities[0]} />
          ) : (
            <>
              <Box mb={2}>
                <Typography variant="h6" color="textPrimary">
                  You haven't setup any availability for this facility.
                </Typography>
              </Box>
              <RouterLinkButton
                variant="contained"
                color="primary"
                to={'/pga-coach/settings/availability'}
              >
                Add Availability
              </RouterLinkButton>
            </>
          )}
        </ContentSection>
      </Container>
    </>
  )
}

const FacilitiesPageShow = props => {
  const { facility } = props.location.state

  if (!facility) return <Error />

  return <Facility facility={facility} />
}

export default FacilitiesPageShow
